import { useMemo, useState } from 'react'
import { auroraStore, useAuroraStore } from '../store/auroraStore'
import { activeChild, activeCycle, blocksForDate, currentPlan, planTheme, weekForDate, type PlanBlock } from '../store/selectors'
import { formatDuration, formatFriendlyDate, todayISO } from '../lib/dates'
import { BlockCard } from '../components/today/BlockCard'
import { DateStrip } from '../components/today/DateStrip'
import { RitualsPanel } from '../components/today/RitualsPanel'
import { DayComplete } from '../components/today/DayComplete'
import { CloseDay } from '../components/today/CloseDay'
import { CheckinBanner } from '../components/checkin/CheckinBanner'
import { TellAurora } from '../components/today/TellAurora'
import { CycleOverview } from '../components/plan/CycleOverview'
import { Sheet } from '../components/ui/Sheet'

/**
 * Today — the plan artifact, rendered as a runnable day.
 *
 * This is a LENS, not a source of truth. The agent owns the plan; if it decides
 * a week should look nothing like the last one, this keeps working, because it
 * renders what is there instead of demanding what it expected.
 */
export function TodayView() {
  const child = useAuroraStore(activeChild)
  const date = useAuroraStore((s: any) => s.ui.selectedDate ?? todayISO(child?.timezone))
  const blocks = useAuroraStore((s: any) => blocksForDate(s, date))
  const artifactTheme = useAuroraStore((s: any) => planTheme(s, date))
  const week = useAuroraStore((s: any) => weekForDate(s, date))
  const cycle = useAuroraStore(activeCycle) as any
  const plan = useAuroraStore(currentPlan)
  const hasPlan = !!cycle || !!plan
  const theme = week?.theme
    ? `Week ${week.number} · ${week.theme}`
    : artifactTheme
  const [planOpen, setPlanOpen] = useState(false)

  const totals = useMemo(() => {
    const pending = blocks.filter((b: PlanBlock) => b.status === 'pending')
    return {
      pending: pending.length,
      total: blocks.length,
      minutes: pending.reduce((sum: number, b: PlanBlock) => sum + (b.minutes ?? 10), 0),
    }
  }, [blocks])

  if (!child) return null

  const ask = (text: string) => {
    const s = auroraStore.getState() as any
    s.setComposing(text)
    s.setView('thread')
  }

  return (
    <div className="page">
      <header className="page-head reveal" style={{ '--i': 0 } as React.CSSProperties}>
        {hasPlan ? (
          <button
            className="page-eyebrow page-eyebrow-btn"
            onClick={() => setPlanOpen(true)}
            aria-haspopup="dialog"
          >
            {theme ? theme : 'The plan'}
            <ChevronIcon />
          </button>
        ) : (
          <div className="page-eyebrow">{relativeDay(date, todayISO(child.timezone))}</div>
        )}
        <h1 className="page-title">{formatFriendlyDate(date, child.timezone)}</h1>
        {totals.total > 0 && (
          <p className="page-sub tnum">
            {relativeDay(date, todayISO(child.timezone))} ·{' '}
            {totals.pending > 0
              ? `${totals.pending} of ${totals.total} to go · about ${formatDuration(totals.minutes)}`
              : `all ${totals.total} settled`}
          </p>
        )}
      </header>

      <TellAurora />

      <CheckinBanner />

      <DateStrip selected={date} timezone={child.timezone} />

      {blocks.length === 0 ? (
        <div className="empty section">
          <p className="empty-title">{plan ? 'Nothing planned for this day' : 'No plan yet'}</p>
          <p className="empty-body">
            {hasPlan
              ? 'A day off is part of the plan too. Ask for something if you want it.'
              : `Ask Aurora to make a plan for ${child.name} — it will use everything it knows so far.`}
          </p>
          <div className="row row-2" style={{ justifyContent: 'center', marginTop: 'var(--s-4)' }}>
            <button
              className="btn btn-primary"
              onClick={() => ask(hasPlan ? `Plan ${date} for ${child.name}` : `Make a plan for ${child.name}`)}
            >
              {hasPlan ? 'Plan this day' : 'Make a plan'}
            </button>
          </div>
        </div>
      ) : (
        <section className="section">
          <div className="stack stack-3">
            {blocks.map((block: PlanBlock, i: number) => (
              <div key={block.id} className="reveal" style={{ '--i': i + 1 } as React.CSSProperties}>
                <BlockCard block={block} date={date} />
              </div>
            ))}
          </div>
        </section>
      )}

      <RitualsPanel date={date} />

      <CloseDay date={date} today={todayISO(child.timezone)} />

      <DayComplete date={date} />

      {hasPlan && (
        <div className="today-foot">
          <button className="btn btn-quiet btn-sm" onClick={() => ask('How is this week going?')}>
            Ask about this week
          </button>
        </div>
      )}

      <Sheet
        open={planOpen}
        onClose={() => setPlanOpen(false)}
        eyebrow="The plan"
        title={child.name ? `${child.name}'s cycle` : 'This cycle'}
        wide
      >
        <CycleOverview />
      </Sheet>
    </div>
  )
}

function relativeDay(date: string, today: string): string {
  if (date === today) return 'Today'
  const d = new Date(`${date}T12:00:00Z`).getTime()
  const t = new Date(`${today}T12:00:00Z`).getTime()
  const diff = Math.round((d - t) / 86_400_000)
  if (diff === 1) return 'Tomorrow'
  if (diff === -1) return 'Yesterday'
  return new Date(`${date}T12:00:00Z`).toLocaleDateString('en-US', { weekday: 'long', timeZone: 'UTC' })
}

const ChevronIcon = () => (
  <svg
    width="12"
    height="12"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth={2.5}
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
  >
    <path d="M9 6l6 6-6 6" />
  </svg>
)
